import { KeepAwake } from "@capacitor-community/keep-awake";
import { Capacitor } from "@capacitor/core";

/**
 * HOLD THE SCREEN AWAKE WHILE A PIECE IS RUNNING (BUD-284).
 *
 * A screen that sleeps at minute 4 of a 20-minute metcon is a useless clock. The athlete's
 * hands are full, the phone is on the floor two metres away, and waking it costs a walk, a
 * tap, and whatever the timer said in the meantime. This is the one capability in the seam
 * whose absence turns the app's most important screen into a decoration.
 *
 * TWO IMPLEMENTATIONS, AND THEY FAIL DIFFERENTLY — which is the whole reason this is a seam
 * module rather than eight lines in the timer.
 *
 *   - ON DEVICE it is `@capacitor-community/keep-awake`, which sets iOS's
 *     `UIApplication.isIdleTimerDisabled`. That flag belongs to the APP, survives the app
 *     being backgrounded and restored, and is not quietly revoked — so the native path needs
 *     no re-acquire and has none.
 *   - ON THE WEB it is the browser's own Screen Wake Lock API, and that one IS quietly
 *     revoked: the browser drops a screen lock whenever the page is hidden and never hands
 *     it back on its own. So one glance at a notification kills the screen for the rest of
 *     the workout — and it looks perfect in every manual test, because it holds right up
 *     until the first interruption, which is exactly when it was needed. The
 *     `visibilitychange` listener below is that half, and it is not optional.
 *
 * A REFUSED LOCK IS NOT AN ERROR. Low battery, an unsupported browser, an insecure origin,
 * a simulator: the request rejects and the piece carries on with a screen that may sleep.
 * Interrupting a workout over it would be worse than the sleep. So {@link ScreenLock.enable}
 * ANSWERS rather than throws — it reports whether the lock is actually HELD, never merely
 * asked for, because a badge claiming the screen is held while it dims is worse than no
 * badge at all.
 *
 * ⚠️ THE ANSWER IS A SNAPSHOT, NOT A SUBSCRIPTION. On the web the browser may drop the lock
 * while the page is hidden; the listener re-takes it on return, but a caller holding the
 * boolean from a call made ten minutes ago is holding a value that was true at the time. Ask
 * again — or, on the one screen that renders a badge, ask on the tick it already runs.
 *
 * ⚠️ NOT VERIFIED ON A PHONE. That the screen actually stays awake through a fifteen-minute
 * piece cannot be proven here: a simulator never sleeps and jsdom has no screen. The web
 * half below is what this project's unit suite exercises. See `docs/device-checklist.md`.
 */
export interface ScreenLock {
  /**
   * Ask for the screen to stay lit, and answer whether it is now actually held.
   *
   * Idempotent: calling it while already held re-checks rather than stacking locks.
   */
  enable(): Promise<boolean>;
  /** Let the screen sleep again. Safe to call when nothing is held. */
  disable(): Promise<void>;
}

const isNativeRuntime = Capacitor.isNativePlatform();

/* -----------------------------------------------------------------------------------------
 * THE WEB HALF
 * -------------------------------------------------------------------------------------- */

/** The live lock, or null. This is the thing `release()` acts on. */
let sentinel: WakeLockSentinel | null = null;

/**
 * Does the CALLER still want the screen held?
 *
 * Separate from `sentinel` because the two disagree for the whole time the page is hidden:
 * the browser has taken the lock away, and the answer to "should we take it back when we
 * are looked at again" is this flag rather than the (now null) sentinel.
 */
let wanted = false;

/** Registered once, for as long as anything wants the screen. */
let listening = false;

async function acquireWebLock(): Promise<boolean> {
  if (!("wakeLock" in navigator)) return false;
  try {
    const granted = await navigator.wakeLock.request("screen");

    /**
     * 🔑 THE CALLER MAY HAVE LET GO WHILE THE REQUEST WAS IN FLIGHT — a Start immediately
     * followed by a Pause, or a screen that unmounts on the frame after it mounted. By then
     * `disable()` has already nulled the sentinel and dropped the listener, so storing this
     * one would leave a LIVE LOCK NOBODY IS TRACKING: nothing would ever release it, the
     * phone would stop dimming for the rest of the session, and the badge would honestly
     * report "not held" the whole time. Hand it straight back instead.
     */
    if (!wanted) {
      await granted.release().catch(() => {});
      return false;
    }

    sentinel = granted;
    /**
     * The browser tells us when it takes the lock away. Clearing the sentinel here is what
     * keeps a later `enable()` from believing a released lock is still live and answering
     * `true` without asking for anything.
     */
    sentinel.addEventListener("release", () => {
      sentinel = null;
    });
    return true;
  } catch {
    // Denied: low battery, an insecure origin, a browser without the API. The piece still
    // runs; the screen may just sleep.
    return false;
  }
}

function onVisible() {
  if (document.visibilityState !== "visible") return;
  if (!wanted || sentinel != null) return;
  void acquireWebLock();
}

/* -----------------------------------------------------------------------------------------
 * THE SEAM
 * -------------------------------------------------------------------------------------- */

export const keepAwake: ScreenLock = {
  async enable() {
    if (isNativeRuntime) {
      try {
        // `isSupported` first: the plugin answers false rather than throwing on a platform
        // that cannot do this, and a `keepAwake()` on such a platform is a rejected promise
        // rather than a useful failure.
        const { isSupported } = await KeepAwake.isSupported();
        if (!isSupported) return false;
        await KeepAwake.keepAwake();
        const { isKeptAwake } = await KeepAwake.isKeptAwake();
        return isKeptAwake;
      } catch {
        return false;
      }
    }

    wanted = true;
    if (!listening) {
      document.addEventListener("visibilitychange", onVisible);
      listening = true;
    }
    if (sentinel != null) return true;
    return acquireWebLock();
  },

  async disable() {
    if (isNativeRuntime) {
      try {
        await KeepAwake.allowSleep();
      } catch {
        // Nothing to allow, or a platform that never disabled it. Either way the screen is
        // free to sleep, which is what was asked for.
      }
      return;
    }

    wanted = false;
    if (listening) {
      document.removeEventListener("visibilitychange", onVisible);
      listening = false;
    }
    const held = sentinel;
    sentinel = null;
    await held?.release().catch(() => {});
  },
};
